/**
 * Contraste des couleurs, selon la méthode des WCAG 2.2.
 *
 * L'exigence d'accessibilité étant élevée (ACCESSIBILITE.md), le contraste ne
 * doit pas dépendre du coup d'œil de celui qui choisit les couleurs : il se
 * calcule et se vérifie automatiquement.
 */

/** Seuil pour du texte courant. */
export const AA_TEXT = 4.5;
/** Seuil pour du gros texte. */
export const AA_LARGE_TEXT = 3;
/**
 * Seuil des éléments non textuels — pastilles de points d'intérêt, tracés,
 * bordures de champs (critère 1.4.11).
 */
export const AA_NON_TEXT = 3;

function parseHex(color: string): [number, number, number] {
  const hex = color.replace("#", "");
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;

  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    throw new TypeError(`Couleur hexadécimale invalide : ${color}`);
  }

  return [
    Number.parseInt(full.slice(0, 2), 16),
    Number.parseInt(full.slice(2, 4), 16),
    Number.parseInt(full.slice(4, 6), 16),
  ];
}

/** Luminance relative, telle que définie par les WCAG. */
export function relativeLuminance(color: string): number {
  const channels = parseHex(color).map((value) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/** Rapport de contraste entre deux couleurs, de 1 (identiques) à 21. */
export function contrastRatio(foreground: string, background: string): number {
  const a = relativeLuminance(foreground);
  const b = relativeLuminance(background);
  const [lighter, darker] = a > b ? [a, b] : [b, a];
  return (lighter + 0.05) / (darker + 0.05);
}

export function meetsContrast(
  foreground: string,
  background: string,
  threshold: number = AA_TEXT,
): boolean {
  return contrastRatio(foreground, background) >= threshold;
}
